import { useEffect } from "react";

import type { UseFormReset } from "react-hook-form";
import { useHistory } from "react-router-dom";

import { useGetOAuthCreatedClients } from "~/v2/api/hooks";
import {
  OAUTH_CLIENT_CATEGORIES,
  type OAuthClientCategory,
  type OAuthClientResponse,
} from "~/v2/api/oauth";
import { Path } from "~/v2/path";
import type { SubmitAppFormValues } from "../SubmitApp.schema";
import type { SubmitAppMode } from "../SubmitApp.types";

const getEditValues = (client: OAuthClientResponse): SubmitAppFormValues => ({
  name: client.name,
  redirect_uri: client.redirect_uri ?? [],
  description: client.description,
  url: client.url,
  terms_of_use: client.terms_of_use,
  privacy_policy: client.privacy_policy,
  logo: client.logo ?? "",
  // The loose-parsed response types `status`/`categories` as plain strings so a
  // new backend value never drops the record; narrow them back to the form's
  // enums here, discarding any value the form can't represent.
  status: client.status === "ACTIVE" ? "ACTIVE" : "DISABLED",
  screenshots: client.screenshots ?? [],
  categories: (client.categories ?? []).filter(
    (category): category is OAuthClientCategory =>
      (OAUTH_CLIENT_CATEGORIES as readonly string[]).includes(category),
  ),
});

interface UseSubmitAppClientLoadOptions {
  clientId?: string;
  reset: UseFormReset<SubmitAppFormValues>;
}

export const useSubmitAppClientLoad = ({
  clientId,
  reset,
}: UseSubmitAppClientLoadOptions) => {
  const { replace } = useHistory();
  const mode: SubmitAppMode = clientId ? "edit" : "create";
  const { clients, isError, isFetching, isLoading, isSuccess, refetch } =
    useGetOAuthCreatedClients();

  useEffect(() => {
    if (!clientId || !isSuccess || isError) {
      return;
    }

    const editClient = clients?.find((client) => client.client_id === clientId);
    if (!editClient) {
      replace(Path.ROUTE_ALPACA_CONNECT_SUBMIT);
      return;
    }

    reset(getEditValues(editClient));
  }, [clientId, clients, isError, isSuccess, replace, reset]);

  return {
    mode,
    isLoading,
    hasEditLoadError: mode === "edit" && isError,
    isRetryingEditLoad: mode === "edit" && isFetching,
    onRetryEditLoad: () => {
      void refetch();
    },
  };
};
